import React, { useState, useEffect } from 'react';
import MakeRequest from '../Helpers/MakeRequest';
import { Container, Row, Col, Table, Button, Form } from 'react-bootstrap';
import { useOutletContext } from 'react-router-dom';
import toBlob from '../Helpers/FileBlobs';
import axios from 'axios';
import apiUrl from '../Helpers/APIWrapper';


const EditReports = () => {
    const { l } = useOutletContext();
    const [references, setReferences] = useState([]);
    const [saveDisabled, setSaveDisabled] = useState(true);
    const [pageContent, setPageContent] = useState({
        ingressSwe: '',
        ingressEng: ''
    });

    useEffect(() => {
        const fetchReferences = async () => {
            try {
                const response = await MakeRequest(null, `content/references`, 'get');
                if (response.data) {
                    setReferences(response.data.filter(obj => obj.type === "Reports"));
                }
            } catch (error) {
                console.error("Failed to fetch references:", error);
            }
        };
        
        const fetchPageContent = async () => {
            try {
                const response = await MakeRequest(null, `content/pageContent?page=reports`, 'get');
                if (response.data && response.data[0]) {
                    setPageContent({
                        ingressSwe: response.data[0].ingressSwe || '',
                        ingressEng: response.data[0].ingressEng || ''
                    });
                }
            } catch (error) {
                console.error("Failed to fetch page content:", error);
            }
        };
        
        fetchReferences();
        fetchPageContent();
    }, []);

    const handleChange = (e, idx, field) => {
        if (field === 'order' && e.target.value && e.target.value < 1) return; // Prevent order less than 1

        setSaveDisabled(false);
        const { value } = e.target;
        setReferences(prev =>
            prev.map((item, i) =>
                i === idx ? { ...item, [field]: value } : item
            )
        );
    };

    const handlePageContentChange = (field, value) => {
        setSaveDisabled(false);
        setPageContent(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSaveChanges = async () => {
        try {

            const formData = new FormData();

            for (const [index, reference] of references.entries()) {

                if (Array.isArray(reference.fileContent)) {
                    for (const [fileIndex, file] of reference.fileContent.entries()) {
                        if (file.data && file.data.startsWith("data:")) {
                            const blob = toBlob(file.data);
                            const name = file?.name ?? `referenceFile-${index}-${fileIndex}`;
                            formData.append('files', blob, name);
                            formData.append(`referenceFileName-${index}-${fileIndex}`, name);
                        } else {
                            formData.append(`referenceFileName-${index}-${fileIndex}`, file.name || '');
                            formData.append(`referenceFilePath-${index}-${fileIndex}`, file.file || '');
                        }

                        formData.append(`referenceFileTitleSv-${index}-${fileIndex}`, file.titleSv || '');
                        formData.append(`referenceFileTitleEn-${index}-${fileIndex}`, file.titleEn || '');
                    }
                }

                formData.append(`references[${index}][company]`, reference.company);
                formData.append(`references[${index}][link]`, reference.link);
                formData.append(`references[${index}][type]`, "Reports");
                formData.append(`references[${index}][titleSwe]`, reference.titleSwe);
                formData.append(`references[${index}][contentSwe]`, reference.contentSwe);
                formData.append(`references[${index}][titleEng]`, reference.titleEng);
                formData.append(`references[${index}][contentEng]`, reference.contentEng);
                formData.append(`references[${index}][order]`, reference.order || 9999);
                formData.append(`id-${index}`, reference._id);
            };

            const response = await axios.post(`${apiUrl}/content/references`, formData, {
                withCredentials: true,
            });
            
            // Save page content
            const pageContentResponse = await MakeRequest(
                { ingressSwe: pageContent.ingressSwe, ingressEng: pageContent.ingressEng },
                'content/pageContent?page=reports',
                'post'
            );
            
            if (response.status === 200 && pageContentResponse.status === 200) {
                alert(l("Ändringar sparade!", "Changes saved!"));
                setSaveDisabled(true);
                //window.location.reload()
            } else {
                alert(l("Misslyckades med att spara ändringar.", "Failed to save changes."));
            }

        } catch (error) {
            console.error("Validation error:", error);
            alert(l("Ett fel uppstod vid validering av data.", "An error occurred while validating data."));
            return;
        };
    };

    const handleAddItem = () => {
        setSaveDisabled(false);
        // Find the highest existing order value and set new one to +1
        const maxOrder = references?.length > 0
            ? Math.max(...references?.map(r => Number(r.order) || 0))
            : 0;
        setReferences([
            ...references,
            { company: '', link: '', title: '', content: '', type: '', fileContent: [] }
        ]);
    };

    const handleDeleteItem = async (idx) => {
        if (!references[idx]._id) {
            setReferences(references?.filter((_, i) => i !== idx));
            return;
        }

        if (window.confirm(l("Är du säker på att du vill ta bort denna referens?", "Are you sure you want to delete this reference?"))) {
            try {
                const res = await MakeRequest({ id: references[idx]._id }, `content/references/${references[idx]._id}`, 'delete');
                if (res.status === 200) {
                    setReferences(references?.filter((_, i) => i !== idx));
                    alert(l("Referens borttagen.", "Reference deleted successfully"));
                } else {
                    alert(l("Misslyckades med att ta bort referens.", "Failed to delete reference"));
                }
            } catch (error) {
                console.error("Failed to delete reference:", error);
            }
        }
    };

    const handleFileChange = (e, idx) => {
        setSaveDisabled(false);
        const files = Array.from(e.target.files);
        for (const file of files) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const content = e.target.result;
                const newReferences = [...references];
                newReferences[idx].fileContent = [...newReferences[idx].fileContent, {
                    data: content,
                    name: file.name,
                    type: file.type
                }];
                setReferences(newReferences);
                // Do something with the file content
            };
            reader.readAsDataURL(file);
        };
    };

    const handleFileTitleChange = (e, referenceIdx, fileIdx, titleField) => {
        setSaveDisabled(false);
        const newReferences = [...references];
        newReferences[referenceIdx].fileContent[fileIdx][titleField] = e.target.value;
        setReferences(newReferences);
    };

    const handleDeleteFile = (referenceIdx, fileIdx) => {
        setSaveDisabled(false);
        const newReferences = [...references];
        newReferences[referenceIdx].fileContent = newReferences[referenceIdx].fileContent.filter((_, i) => i !== fileIdx);
        setReferences(newReferences);
    }
    

    return (
        <>
            <Container style={{ border: "1px solid #ccc", padding: "20px", marginBottom: "20px", borderRadius: "5px", backgroundColor: "white" }}>
                <h5>{l("Sidinformation", "Page Information")}</h5>
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label><b>{l("Ingress (Svenska)", "Ingress (Swedish)")}</b></Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={5}
                                placeholder={l("Skriv ingress på svenska", "Write ingress in Swedish")}
                                value={pageContent.ingressSwe}
                                onChange={(e) => handlePageContentChange('ingressSwe', e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label><b>{l("Ingress (Engelska)", "Ingress (English)")}</b></Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={5}
                                placeholder={l("Skriv ingress på engelska", "Write ingress in English")}
                                value={pageContent.ingressEng}
                                onChange={(e) => handlePageContentChange('ingressEng', e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>
            </Container>
            <Row>
                <Form.Label column style={{ display: "flex", alignItems: "center", fontWeight: "bold" }}>
                    {l("Lägg till referens", "Add reference")}
                    <Button
                        size="sm"
                        style={{ marginLeft: 10 }}
                        onClick={handleAddItem}
                        title={l("Lägg till ny rad", "Add new row")}
                    >
                        <i className="fa-solid fa-plus"></i>
                    </Button>
                </Form.Label>
                {references.length === 0 && (
                    <Row style={{ color: "#888", fontStyle: "italic", padding: "10px 0" }} className='mx-1 p-0'>
                        {l("Inga referenser har lagts till.", "No references have been added.")}
                    </Row>
                )}

                {references.map((reference, idx) => (
                    <Container key={reference._id || idx} style={{ border: "1px solid #ccc", padding: "10px", marginBottom: "10px", borderRadius: "5px", backgroundColor: "white" }} className='p-0 m-0 mt-3'>

                        <Row className='m-0 p-0'>
                            <Col xl={4} md={12} style={{ border: "1px solid lightgrey" }} className='p-2'>
                                <Form.Label><b>{l("Företag", "Company")}</b></Form.Label>
                                <Form.Group controlId={`formCompany${idx}`}>
                                    <Form.Control
                                        type="text"
                                        placeholder={l("Företag", "Company")}
                                        onChange={(e) => handleChange(e, idx, "company")}
                                        value={reference.company}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xl={4} md={12} style={{ border: "1px solid lightgrey" }} className='p-2'>
                                <Form.Label><b>{l("Länk", "Link")}</b></Form.Label>
                                <Form.Group controlId={`formLink${idx}`}>
                                    <Form.Control
                                        type="text"
                                        placeholder={l("Länk", "Link")}
                                        onChange={(e) => handleChange(e, idx, "link")}
                                        value={reference.link}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xl={4} md={12} style={{ border: "1px solid lightgrey" }} className='p-2'>
                                <Form.Label><b>{l("Sorteringsordning", "Sort Order")}</b></Form.Label>
                                <Form.Group controlId={`formOrder${idx}`}>
                                    <Form.Control
                                        type="number"
                                        min="1"
                                        placeholder={l("Ordning", "Order")}
                                        onChange={(e) => handleChange(e, idx, "order")}
                                        value={reference.order || ''}
                                    />
                                </Form.Group>
                            </Col>
{/*                             <Col xl={4} md={12} style={{ border: "1px solid lightgrey" }} className='p-2'>
                                <Form.Label><b>{l("Typ", "Type")}</b></Form.Label>
                                <Form.Group controlId={`formType${idx}`}>
                                    <Form.Select
                                        onChange={(e) => handleChange(e, idx, "type")}
                                        value={reference.type || "hej"}
                                    >
                                        <option value="">{l("Välj typ", "Select type")}</option>
                                        <option value="Customer Testimonials">{l("Kundutlåtanden", "Customer Testimonials")}</option>
                                        <option value="Reports">{l("Rapporter", "Reports")}</option>
                                        <option value="Tests">{l("Tester", "Tests")}</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col> */}
                        </Row>

                        <Row className='m-0 p-0'>
                            <Col xl={6} md={12} style={{ border: "1px solid lightgrey" }} className='p-2'>
                                <Form.Label><b>{l("Svenska", "Swedish")}</b></Form.Label>
                                <Form.Group>
                                    <Form.Label><b>{l("Titel", "Title")}</b></Form.Label>
                                    <Form.Control placeholder={l("Ange titel", "Enter title")} type="text" value={reference.titleSwe} onChange={(e) => handleChange(e, idx, "titleSwe")} />
                                    <Form.Label><b>{l("Innehåll", "Content")}</b></Form.Label>
                                    <Form.Control
                                        placeholder={l("Ange innehåll", "Enter content")}
                                        as="textarea"
                                        rows={3}
                                        value={reference.contentSwe}
                                        onChange={(e) => handleChange(e, idx, "contentSwe")}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xl={6} md={12} style={{ border: "1px solid lightgrey" }} className='p-2'>
                                <Form.Label><b>{l("Engelska", "English")}</b></Form.Label>
                                <Form.Group>
                                    <Form.Label><b>{l("Titel", "Title")}</b></Form.Label>
                                    <Form.Control placeholder={l("Ange titel", "Enter title")} type="text" value={reference.titleEng} onChange={(e) => handleChange(e, idx, "titleEng")} />
                                    <Form.Label><b>{l("Innehåll", "Content")}</b></Form.Label>
                                    <Form.Control
                                        placeholder={l("Ange innehåll", "Enter content")}
                                        as="textarea"
                                        rows={3}
                                        value={reference.contentEng}
                                        onChange={(e) => handleChange(e, idx, "contentEng")}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className='m-0 p-0'>
                            <Row className='m-0 p-0 my-2'>
                                <Container>
                                    <Form.Label><b>{l("Filer", "Files")}</b></Form.Label>
                                    <input
                                        id={`file-input-${idx}`}
                                        type="file"
                                        multiple
                                        style={{ display: "none" }}
                                        onChange={e => handleFileChange(e, idx)}
                                    />

                                    <label htmlFor={`file-input-${idx}`}>
                                        <Button
                                            as="span"
                                            size="sm"
                                            style={{ marginLeft: 10 }}
                                            title={l("Ladda upp filer", "Upload Files")}
                                        >
                                            <i className="fa-solid fa-plus"></i>
                                        </Button>
                                    </label>
                                </Container>
                            </Row>
                        </Row>
                        <Row className='m-0 p-0'>
                            <Col>
                                {Array.isArray(references[idx].fileContent) && references[idx].fileContent.length > 0 ? (
                                    <>
                                        <Table striped bordered hover className="stack-on-md">
                                            <thead>
                                                <tr>
                                                    <th style={{ fontWeight: "unset" }}>{l("Fil", "File")}</th>
                                                    <th style={{ fontWeight: "unset" }}>{l("Titel Svenska", "Title Swedish")}</th>
                                                    <th style={{ fontWeight: "unset" }}>{l("Titel Engelska", "Title English")}</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {references[idx].fileContent.map((file, fileIdx) => (
                                                    <tr key={fileIdx}>
                                                        <td>
                                                            {typeof file === 'string' ? file.split("-").slice(2).join("-") : file.name}
                                                        </td>
                                                        <td><Form.Control type="text" value={file.titleSv} onChange={e => handleFileTitleChange(e, idx, fileIdx, 'titleSv')} /></td>
                                                        <td><Form.Control type="text" value={file.titleEn} onChange={e => handleFileTitleChange(e, idx, fileIdx, 'titleEn')} /></td>
                                                        <td>
                                                            <Button
                                                                variant="link"
                                                                onClick={() => handleDeleteFile(idx, fileIdx)}
                                                            >
                                                                <i className="fa-solid fa-times" style={{ color: "red" }}></i>
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </>
                                ) : (<div style={{ color: "#888", fontStyle: "italic", padding: "10px 0" }}>
                                    {l("Inga filer har lagts till.", "No files have been added.")}
                                </div>)}
                            </Col>
                        </Row>
                        <Row className="justify-content-center pb-3">
                            <Col className="d-flex justify-content-center">
                                <Button
                                    size="md"
                                    variant="danger"
                                    title={l("Ta bort produkt", "Delete product")}
                                    onClick={() => handleDeleteItem(idx)}
                                >
                                    <i className="fa-solid fa-trash"></i>
                                </Button>
                            </Col>
                        </Row>
                    </Container>
                ))}
            </Row>

            <Row className="d-flex justify-content-center">
                <Button style={{ width: "200px" }} onClick={handleSaveChanges} disabled={saveDisabled}>
                    {l("Spara ändringar", "Save Changes")}
                </Button>
            </Row>
        </>
    );
};

export default EditReports;
